<template>
  <div class="home-page">
    <div v-if="hunt" class="home-resume">
      <b-button style="margin: 8px" @click="nagivateToEitherIntroOrHunt()" type="is-primary">{{
        $t("home.go_to_hunt")
      }}</b-button>
      <b-button style="margin: 8px" @click="clearCache" type="is-info">{{
        $t("home.clear_all_data")
      }}</b-button>
    </div>
    <template v-else>
      <div class="home-content">
        <img :src="bannerImage" alt="Keelung For A Walk" class="home-banner" />
        <b-field :label="$t('home.access_code_label')" class="home-code-field">
          <b-input
            v-model="code"
            @keyup.enter="enterCode"
            :placeholder="$t('home.enter_code_here')"
            class="home-code-input"
            rounded
          />
        </b-field>
        <b-button @click="enterCode" type="is-primary" class="home-login-button">{{
          $t("home.login_button")
        }}</b-button>
      </div>
      <div class="home-links">
        <a :href="mobileSetupUrl" target="_blank" rel="noopener noreferrer">{{
          $t("home.mobile_setup_link")
        }}</a>
        <a :href="aboutExperienceUrl" target="_blank" rel="noopener noreferrer">{{
          $t("home.about_experience_link")
        }}</a>
      </div>
    </template>
  </div>
</template>

<script>
import { HuntService } from "@/services/hunt.service";
import { UserService } from "@/services/user.service";
import ClientStorageService from "@/services/client-storage-services";
import bannerImage from "@/assets/keelung-banner.png";

const MOBILE_SETUP_URL = {
  zh: "https://keelung-for-a-walk.com/zh/treasurehuntsetting/",
  en: "https://keelung-for-a-walk.com/game-audio-guide-instructions/",
};
const ABOUT_EXPERIENCE_URL = {
  zh: "https://keelung-for-a-walk.com/zh/treasure-hunt/",
  en: "https://keelung-for-a-walk.com/tours/",
};

export default {
  name: "Home",
  data() {
    return {
      code: "",
      hunt: "",
      isComponentModalActive: false,
      bannerImage,
    };
  },
  computed: {
    mobileSetupUrl() {
      return MOBILE_SETUP_URL[this.$i18n.locale] || MOBILE_SETUP_URL.en;
    },
    aboutExperienceUrl() {
      return ABOUT_EXPERIENCE_URL[this.$i18n.locale] || ABOUT_EXPERIENCE_URL.en;
    },
  },
  created() {
    const huntItem = ClientStorageService.getCurrentHunt();
    if (huntItem) {
      UserService.signInAnon();
      this.hunt = huntItem;
    }
  },
  methods: {
    async enterCode() {
      const loadingComponent = this.$buefy.loading.open({
        container: this.isFullPage,
      });
      const response = await HuntService.getHuntByCode(this.code);
      loadingComponent.close();
      if (response === "INVALID_CODE") {
        this.$buefy.dialog.alert({
          title: this.$t("error.wrong_access_code_title"),
          message: this.$t("error.wrong_access_code_message"),
        });
      } else if (response === "OFFLINE") {
        this.$buefy.dialog.alert({
          title: this.$t("error.offline_title"),
          message: this.$t("error.offline_message"),
        });
      } else if (response === "NO_CODES_LEFT") {
        this.$buefy.dialog.alert({
          title: this.$t("error.no_codes_left_title"),
          message: this.$t("error.no_codes_left_message"),
        });
      } else if (response === "ERROR") {
        this.$buefy.dialog.alert({
          title: this.$t("error.unknown_title"),
          message: this.$t("error.unknown_message"),
        });
      } else {
        // no error
        this.hunt = response;
        ClientStorageService.setHuntActivationDateTime();
        this.signInNewHuntUser();
      }
    },
    setChallengeLevelToIntro() {
      ClientStorageService.setCurrentChallengeLevel('Intro');
    },
    nagivateToEitherIntroOrHunt() {
      const currentChallengeLevel = ClientStorageService.getCurrentChallengeLevel();
      const isInIntro = isNaN(currentChallengeLevel);

      if (isInIntro) {
        this.$router.push('/intro');
      } else {
        this.$router.push('/hunt');
      }
    },
    async signInNewHuntUser() {
      const loading = this.$buefy.loading.open({
        container: this.isFullPage,
      });
      await UserService.newUserForHunt(this.hunt);
      ClientStorageService.setCurrentHunt(this.hunt);
      this.setChallengeLevelToIntro()
      this.$router.push("/intro");
      loading.close();
    },
    clearCache() {
      this.hunt = "";
      ClientStorageService.clearHuntCache();
    },
  },
};
</script>

<style scoped>
/*
 * #app (App.vue) is already `display: flex; flex-direction: column; min-height: 100vh`,
 * and this component's root is a direct child of it (via <router-view>). Growing into
 * that remaining space, plus `margin-top: auto` on .home-links below, is what pins the
 * links near the bottom of the viewport without needing a second nested 100vh container.
 */
.home-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 32px;
  /* keep the links clear of the home-indicator / gesture bar on notched phones */
  padding-bottom: max(32px, calc(16px + env(safe-area-inset-bottom, 0px)));
  box-sizing: border-box;
}

.home-resume {
  padding-bottom: 16px;
}

.home-banner {
  display: block;
  width: 100%;
  /* Source asset is 650x260 - capping at its native width keeps it crisp on
     tablet/desktop instead of upscaling it past its real resolution and going soft. */
  max-width: 650px;
  margin: 0 auto 24px;
  border-radius: 8px;
}

@media screen and (max-width: 768px) {
  .home-banner {
    /* full-bleed on mobile: break out of every ancestor's padding by sizing off the
       viewport directly, so it reaches both edges regardless of nesting. */
    width: 100vw;
    max-width: none;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    margin-bottom: 24px;
    border-radius: 0;
  }
}

.home-code-field {
  margin-bottom: 0 !important; /* override Bulma's .field:not(:last-child) spacing below */
}

.home-code-field :deep(.label) {
  margin-bottom: 24px;
}

.home-code-input :deep(.input) {
  max-width: 20rem;
  border-color: #49688d;
}

.home-code-input :deep(.control) {
  max-width: 20rem;
  margin: auto;
}

.home-login-button {
  width: 20rem;
  max-width: 100%;
  margin-top: 24px;
}

.home-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 24px;
}
</style>
